import _ from 'lodash'
import React from 'react'
import Promise from 'bluebird'

import { browserHistory } from "browser/history"
import { Position, Toast } from 'browser/components/atomic-elements/atoms/toast/toast'

import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import apis from '../models/apis'
import { Entity } from 'shared-libs/models/entity'
import { EntityFormPanel } from 'browser/components/atomic-elements/organisms/entity/entity-form-panel'
import { creationSourceFromWeb } from '../utils/utils'
import { ConfirmationModal } from 'browser/components/atomic-elements/organisms/confirmation-modal'

export const enum NavigationPreference {
  KEEP = 'keep',
  REMOVE = 'remove',
}

// null missing results, annoying interface
function n(indexResult) {
  return indexResult === -1 ? null : indexResult
}

export const navigateTo = (entityId: string = undefined, viewId: string = NavigationPreference.KEEP, args: any) => {
  if (!entityId && !viewId) {
    return
  }

  let viewPath = ''
  if (viewId === NavigationPreference.REMOVE) {
    // no-op, default value
  } else if (viewId === NavigationPreference.KEEP) {
    const urlSegments = window.location.pathname.split('/').splice(1)
    // the one time null coalescing is incredible -- expected "0" values
    const lastViewIndex = n(_.findLastIndex(urlSegments, s => s === 'custom')) ?? n(_.findLastIndex(urlSegments, s => s === 'view')) 
      ?? n(_.findLastIndex(urlSegments, s => s === 'search'))
    const stopIndex = n(_.findLastIndex(urlSegments, s => s === 'search')) != null ? lastViewIndex + 1 : lastViewIndex + 2
    viewPath = lastViewIndex !== null ? '/' + urlSegments.slice(0, stopIndex).join('/') : ''
  } else {
    viewPath = `/view/${viewId}`
  }

  let entityPath = ''
  if (entityId !== undefined) {
    entityPath = `/entity/${entityId}`
  }

  const destinationPath = `${viewPath}${entityPath}`

  return browserHistory.push({
    pathname: destinationPath,
    search: window.location.search
  })
}

export const showToast = (message, delay=3000) => {
  const toast = Toast.show({
    message: (
      <div className="c-toastContents">
        <Icon
          className="c-toastContents-icon mr3"
          icon={IconNames.INFO_SIGN}
          size={30}

        />
        <div className="c-toastContents-message">
          {message}
        </div>
      </div>
    ),
    position: Position.BOTTOM_RIGHT,
    isLightMode: true,
  })

  return new Promise((resolve) => {
    setTimeout(() => {
      Toast.dismiss(toast)
      resolve()
    }, delay)
  })
}

export const showConfirmationModal = (
  options: { title?: string; message?: string; yesText?: string; noText?: string } = {}
): Promise<boolean> => {
  return new Promise<boolean>((resolve) => {
    const finish = _.once(resolve)

    ConfirmationModal.open({
      confirmationTitle: options.title ?? 'Confirm Action',
      confirmationText: options.message ?? 'Are you sure you want to continue?',
      primaryButtonText: options.yesText ?? 'Confirm',
      cancelButtonText: options.noText ?? 'Cancel',
      onPrimaryClicked: () => finish(true),
      onClose: () => finish(false),
    })
  })
}


function getEntitySchema(entitySchema) {
  if (!entitySchema) {
    return
  }

  const mappedEntity = _.isString(entitySchema) ? apis.getStore().getRecord(entitySchema) : entitySchema
  return (mappedEntity instanceof Entity) ? mappedEntity : new Entity(mappedEntity, apis)
}

export const showEntityCreationModal = ({ defaultValue, schema }) => {
  const settings = apis.getSettings()
  schema = getEntitySchema(schema)

  const defaultValueWithSource = _.merge(
    { creationSource: creationSourceFromWeb('Table Actions') },
    defaultValue,
  )

  return EntityFormPanel.openAndAwaitSave({
    defaultValue: defaultValueWithSource,
    schema,
    settings,
  })
}

/**
 * Renotify a driver with a new preauthenticated pre-check deeplink, given an
 * identity and context (e.g. cac uniqueId) tuple that matches an existing
 * message record.
 */
export const resendDriverDeeplink = (context: string, identity: string) => {
  if (!context || !identity) {
    console.error(`resendDriverDeeplink missing 'context' or 'identity' args`)
    return
  }
  return apis.resendDeeplink(context, identity)
}
